/**
 * A submitted request registers immediately but is validated asynchronously:
 * the ledger's verdict appears on `GET /head/requests/<id>` as
 * `status.validity` a moment later. Poll for it so the UI can report what
 * actually happened instead of celebrating mere registration.
 *
 * `unknown` = no verdict within the window (report "pending", not success).
 * A rejection carries the ledger's own reason when it gave one.
 */
export type RequestVerdict = "valid" | "invalid" | "unknown";

export interface RequestOutcome {
  verdict: RequestVerdict;
  reason?: string | null;
}

export async function awaitRequestVerdict(
  requestId: number,
  { timeoutMs = 15_000, intervalMs = 600 }: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<RequestOutcome> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const response = await fetch(
        `${window.__APP_CONFIG.apiBaseUrl}/head/requests/${requestId}`,
      );
      if (response.ok) {
        const data = await response.json();
        const validity = data?.status?.validity;
        if (validity === "valid") return { verdict: "valid" };
        if (validity === "invalid") {
          // The ledger records WHY it rejected; hydrozoa never exposed one,
          // which is why this used to be a bare "the exchange rejected it".
          return { verdict: "invalid", reason: data?.failureReason ?? null };
        }
      }
    } catch {
      /* transient -- keep polling until the deadline */
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return { verdict: "unknown" };
}
